import { createSignal, Show } from "solid-js";

export default function InfoModal() {
  const [isOpen, setIsOpen] = createSignal(false);

  return (
    <>
      {/* Info Button */}
      <button
        onClick={() => setIsOpen(true)}
        class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-bold transition-colors flex items-center justify-center border border-white/30"
        title="About IntelliCoach"
      >
        ?
      </button>

      {/* Modal */}
      <Show when={isOpen()}>
        <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="p-6">
              {/* Header */}
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-2xl font-bold text-gray-800">
                  About IntelliCoach
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  class="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Content */}

              <div class="space-y-6 text-gray-700">
                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    ℹ️ About IntelliCoach
                  </h3>
                  <p class="text-sm">
                    IntelliCoach is a proof-of-concept (POC) prototype built to
                    support child welfare professionals during interviews. It
                    provides live transcription, intelligent question
                    suggestions, and post-call summaries to help caseworkers
                    focus on the conversation while ensuring key details are
                    captured.
                  </p>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    👥 Roles & terminology
                  </h3>
                  <ul class="list-disc ml-5 text-sm space-y-1">
                    <li>
                      <strong>Party 1 = Speaker 1</strong> — Interviewer /
                      Caseworker
                    </li>
                    <li>
                      <strong>Party 2 = Speaker 2</strong> — Respondent / Client
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    ⚡ Quick start
                  </h3>
                  <ol class="list-decimal ml-5 space-y-1 text-sm">
                    <li>
                      After login, you’ll land on{" "}
                      <strong>Transcription Sessions</strong>. Use search &
                      filters (date range, status, respondent, type) to find
                      past sessions. Click <strong>New Session</strong> to
                      create one.
                    </li>
                    <li>
                      <strong>Set up your session</strong>:
                      <ul class="list-disc ml-5 mt-1 space-y-1">
                        <li>
                          <strong>Contact Type</strong> (required)
                        </li>
                        <li>
                          <strong>Respondent Name</strong> (required)
                        </li>
                        <li>
                          <strong>Source Language</strong> (required; defaults
                          to English – US)
                        </li>
                        <li>
                          <strong>Target Language</strong> (optional; enables a
                          translation panel on the Transcript tab)
                        </li>
                        <li>
                          <strong>Case #</strong> (optional)
                        </li>
                      </ul>
                    </li>
                    <li>
                      Click <strong>Start</strong> on the main screen.
                    </li>
                    <li>
                      <strong>Start / Pause / Stop</strong> using the banner
                      controls. When finished, click <strong>Stop</strong> to
                      save and generate analysis.
                    </li>
                  </ol>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    🧭 In the workspace
                  </h3>
                  <div class="space-y-3 text-sm">
                    <div>
                      <strong>Interview tab</strong>
                      <ul class="list-disc ml-5 mt-1 space-y-1">
                        <li>
                          <strong>Suggested Questions</strong> Based on a
                          combination of predefined templates (by Contact Type)
                          and live transcript context. Suggestions appear after
                          a few utterances (~30s of speech).
                        </li>
                        <li>
                          <strong>Drag & drop</strong> a suggestion into{" "}
                          <strong>Answered</strong> to mark it asked—this starts
                          capturing an answer.
                        </li>
                        <li>
                          <strong>Dismiss / Restore</strong> to manage the
                          questions during the interview.
                        </li>
                        <li>
                          <strong>Answered</strong> (right): Tracks questions
                          you asked and the captured/edited answers.
                        </li>
                        <li>
                          <strong>Auto-extraction</strong>: while you speak, the
                          app tries to pull answers from recent turns (you can
                          edit anything).
                        </li>
                        <li>
                          <strong>Inline edit</strong>: click a question or
                          answer to fix wording, typos, or add detail.
                        </li>
                      </ul>
                    </div>

                    <div>
                      <strong>Transcript tab</strong>
                      <ul class="list-disc ml-5 mt-1 space-y-1">
                        <li>
                          See the running transcript as chat bubbles labeled{" "}
                          <strong>Party 1</strong> and <strong>Party 2</strong>.
                        </li>
                        <li>
                          If a <strong>Target Language</strong> is selected, a
                          translation panel shows machine translations of
                          utterances not already in that language.
                        </li>
                      </ul>
                    </div>

                    <div>
                      <strong>Analysis tab</strong> (available after you click{" "}
                      <strong>Stop</strong>)
                      <ul class="list-disc ml-5 mt-1 space-y-1">
                        <li>
                          <strong>Summary</strong> of the conversation
                        </li>
                        <li>
                          <strong>Entities</strong> (names, dates, places)
                        </li>
                        <li>
                          <strong>Action items</strong> that you can toggle
                          complete/incomplete
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    🔎 Sessions list
                  </h3>
                  <p class="text-sm">
                    Each row shows <strong>status</strong> (Draft / In progress
                    / Completed), <strong>duration</strong>,{" "}
                    <strong>type</strong>, and a short <strong>summary</strong>.
                    Use <strong>Open</strong> to view a session; completed
                    sessions are read-only.
                  </p>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    ⚠️ Notes & current limitations
                  </h3>
                  <ul class="list-disc ml-5 text-sm space-y-1">
                    This is an early-stage proof of concept designed to showcase
                    what's possible. The UI, features, and logic are not
                    optimized or finalized, they’re meant to spark discussion,
                    test ideas, and can evolve based on client needs or future
                    goals.
                    <li>
                      <strong>Microphone only.</strong>
                    </li>
                    <li>
                      <strong>Contact Type templates</strong> are sample sets to
                      illustrate the workflow; they’re not exhaustive and
                      suggestions also use live context.
                    </li>
                    <li>
                      <strong>Suggestions timing:</strong> Appear after a few
                      utterances, once there’s enough transcript context
                      (typically around 30 seconds of active speech)
                    </li>
                    <li>
                      <strong>Speaker labels:</strong> Speaker identification
                      uses Azure diarization. IDs are session‑local and may
                      differ across sessions, and early turns may be mislabeled
                      before stabilizing.
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    🛡️ Consent & privacy
                  </h3>
                  <p class="text-sm">
                    Obtain verbal consent before recording/transcribing.
                    Transcripts and session metadata are stored with the
                    session.
                  </p>
                </section>

                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    🧰 Troubleshooting
                  </h3>
                  <ul class="list-disc ml-5 text-sm space-y-1">
                    <li>
                      <strong>No transcript?</strong> Allow microphone
                      permissions in the browser, then click{" "}
                      <strong>Start</strong> again.
                    </li>
                    <li>
                      <strong>No suggestions?</strong> Keep speaking for ~30–60
                      seconds; they refresh automatically.
                    </li>
                    <li>
                      <strong>No analysis?</strong> Click <strong>Stop</strong>{" "}
                      to finalize; analysis appears after the session ends.
                    </li>
                    <li>
                      <strong>Stuck UI?</strong> Refresh and reopen the session
                      from the list.
                    </li>
                  </ul>
                </section>
                <section>
                  <h3 class="font-semibold text-lg mb-2 text-blue-600">
                    📫 Feedback
                  </h3>
                  <p class="text-sm">
                    Questions or suggestions? Email{" "}
                    <a
                      href="mailto:ritika_pateriya@redmane.com"
                      class="text-blue-600 underline"
                    >
                      ritika_pateriya@redmane.com
                    </a>
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div class="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setIsOpen(false)}
                  class="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}
